/** @jsxImportSource @emotion/react */

import React, { ReactNode } from 'react';
import { CSSObject } from '@emotion/react';
import AutoOrientationService from '../../../services/autoOrientation';
import { CSSProp } from '../../../types/emotion';

export type HeadingStyles =
  | 'header'
  | 'subheader'
  | 'title'
  | 'minor'
  | '6xl'
  | '5xl'
  | '4xl'
  | '3xl'
  | '2xl'
  | 'xl'
  | 'l';

export interface HeadingProps {
  /**
   * Heading style<br />
   * `header`: Text 2XL Bold (Bosch Styleguide)<br />
   * `subheader`: Text XL Bold (Bosch Styleguide)<br />
   * `title`: Text L Bold (Bosch Styleguide)
   */
  style?: HeadingStyles;
  /**
   * Heading content
   */
  children?: ReactNode;
}

/**
 * The `Heading` component displays a heading with a specific style.
 */
export function Heading({
  style,
  children,
  ...props
}: HeadingProps & Omit<Partial<React.HTMLAttributes<HTMLHeadingElement>>, keyof HeadingProps> & CSSProp) {
  const commonStyles = {
    fontFamily: 'Bosch Sans, sans-serif',
    fontWeight: 'bold',
    whiteSpace: 'pre-line',
    margin: 0,
    padding: 0,
  };

  switch (style) {
    case 'subheader':
      return (
        <h2
          {...props}
          css={
            {
              ...commonStyles,
              ...AutoOrientationService.getStyles({ fontSize: '1.25rem' }, { fontSize: '1.5rem' }),
            } as CSSObject
          }
        >
          {children}
        </h2>
      );
    case 'title':
      return (
        <h3
          {...props}
          css={
            {
              ...commonStyles,
              ...AutoOrientationService.getStyles({ fontSize: '1rem' }, { fontSize: '1.25rem' }),
            } as CSSObject
          }
        >
          {children}
        </h3>
      );
    case 'minor':
      return (
        <h4
          {...props}
          css={
            {
              ...commonStyles,
              fontSize: '1rem',
            } as CSSObject
          }
        >
          {children}
        </h4>
      );
    case '6xl':
      return (
        <h1
          {...props}
          css={
            {
              ...commonStyles,
              fontSize: '5rem',
              lineHeight: '5.75rem',
            } as CSSObject
          }
        >
          {children}
        </h1>
      );
    case '5xl':
      return (
        <h1
          {...props}
          css={
            {
              ...commonStyles,
              fontSize: '4rem',
              lineHeight: '4.8rem',
            } as CSSObject
          }
        >
          {children}
        </h1>
      );
    case '4xl':
      return (
        <h1
          {...props}
          css={
            {
              ...commonStyles,
              fontSize: '3rem',
              lineHeight: '3.75rem',
            } as CSSObject
          }
        >
          {children}
        </h1>
      );
    case '3xl':
      return (
        <h2
          {...props}
          css={
            {
              ...commonStyles,
              fontSize: '2.5rem',
              lineHeight: '3.25rem',
            } as CSSObject
          }
        >
          {children}
        </h2>
      );
    case '2xl':
      return (
        <h3
          {...props}
          css={
            {
              ...commonStyles,
              fontSize: '2rem',
              lineHeight: '2.7rem',
            } as CSSObject
          }
        >
          {children}
        </h3>
      );
    case 'xl':
      return (
        <h4
          {...props}
          css={
            {
              ...commonStyles,
              fontSize: '1.5rem',
              lineHeight: '2.1rem',
            } as CSSObject
          }
        >
          {children}
        </h4>
      );
    case 'l':
      return (
        <h4
          {...props}
          css={
            {
              ...commonStyles,
              fontSize: '1.25rem',
              lineHeight: '1.875rem',
            } as CSSObject
          }
        >
          {children}
        </h4>
      );
    default: // 'header'
      return (
        <h1
          {...props}
          css={
            {
              ...commonStyles,
              ...AutoOrientationService.getStyles({ fontSize: '1.5rem' }, { fontSize: '2rem' }),
            } as CSSObject
          }
        >
          {children}
        </h1>
      );
  }
}
